import React, { useEffect, useState, useCallback, useRef } from 'react';
import {
  getPointageJour, upsertPointage, batchPointage, deletePointage,
  getPointageStats, getPointageAgent,
  getPointageParametres, updatePointageParametres,
  getDepartements,
} from '../services/api';
import ExportPointage from '../components/ExportPointage';
// styles.css est importé globalement dans index.js — aucun import CSS local nécessaire

// ── Constantes ────────────────────────────────────────────────
const STATUTS = ['Present', 'Absent', 'Retard', 'Demi-journee', 'Conge', 'Mission'];
const STATUT_LABELS = {
  Present:      'Présent',
  Absent:       'Absent',
  Retard:       'Retard',
  'Demi-journee': 'Demi-journée',
  Conge:        'Congé',
  Mission:      'Mission',
  'Non saisi':  'Non saisi',
};
const STATUT_COLORS = {
  Present:        { bg: '#e8f5e9', color: '#2e7d32', dot: '#43a047' },
  Absent:         { bg: '#ffebee', color: '#c62828', dot: '#ef5350' },
  Retard:         { bg: '#fff8e1', color: '#f57f17', dot: '#ffa726' },
  'Demi-journee': { bg: '#e3f2fd', color: '#1565c0', dot: '#42a5f5' },
  Conge:          { bg: '#f3e5f5', color: '#6a1b9a', dot: '#ab47bc' },
  Mission:        { bg: '#e0f7fa', color: '#006064', dot: '#26c6da' },
  'Non saisi':    { bg: '#f5f5f5', color: '#757575', dot: '#bdbdbd' },
};

// ── Helpers ───────────────────────────────────────────────────
const today = () => new Date().toISOString().split('T')[0];
const fmtDate = (d) => {
  if (!d) return '—';
  const [y, m, j] = d.split('-');
  return `${j}/${m}/${y}`;
};
const fmtTime = (t) => (t ? t.slice(0, 5) : '—');
const fmtPct  = (v) => (v == null ? '—' : `${v}%`);

function StatutBadge({ statut }) {
  const s = statut || 'Non saisi';
  const c = STATUT_COLORS[s] || STATUT_COLORS['Non saisi'];
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: 5,
      background: c.bg, color: c.color,
      padding: '3px 10px', borderRadius: 20,
      fontSize: 12, fontWeight: 600, whiteSpace: 'nowrap',
    }}>
      <span style={{ width: 6, height: 6, borderRadius: '50%', background: c.dot, flexShrink: 0 }} />
      {STATUT_LABELS[s] || s}
    </span>
  );
}

function Toast({ msg, type, onClose }) {
  useEffect(() => { const t = setTimeout(onClose, 3500); return () => clearTimeout(t); }, [onClose]);
  return <div className={`toast toast-${type}`}>{msg}</div>;
}

// ── Mini Graphe SVG ──────────────────────────────────────────
function MiniSparkline({ data, color = '#1565C0', label = '' }) {
  if (!data || data.length < 2) return null;
  const W = 220, H = 55;
  const vals = data.map(d => parseFloat(d.taux || d.presents || 0));
  const max = Math.max(...vals, 1);
  const pts = vals.map((v, i) => {
    const x = (i / (vals.length - 1)) * (W - 16) + 8;
    const y = H - 12 - ((v / max) * (H - 24));
    return `${x},${y}`;
  });
  const area = `M ${pts.join(' L ')} L ${(vals.length - 1) / (vals.length - 1) * (W - 16) + 8},${H - 12} L 8,${H - 12} Z`;
  return (
    <svg width={W} height={H} style={{ display: 'block' }}>
      <defs>
        <linearGradient id={`g${label}`} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={color} stopOpacity={0.18} />
          <stop offset="100%" stopColor={color} stopOpacity={0.01} />
        </linearGradient>
      </defs>
      <path d={area} fill={`url(#g${label})`} />
      <polyline points={pts.join(' ')} fill="none" stroke={color} strokeWidth={2} strokeLinecap="round" />
      {vals.map((v, i) => {
        const x = (i / (vals.length - 1)) * (W - 16) + 8;
        const y = H - 12 - ((v / max) * (H - 24));
        return <circle key={i} cx={x} cy={y} r={3} fill={color} />;
      })}
    </svg>
  );
}

// ── Barre de progression ─────────────────────────────────────
function ProgressBar({ value, color = '#1565C0', showLabel = true }) {
  const v = Math.min(100, Math.max(0, parseFloat(value) || 0));
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
      <div style={{ flex: 1, background: '#e8edf3', borderRadius: 4, height: 6, overflow: 'hidden' }}>
        <div style={{ width: `${v}%`, height: '100%', background: color, borderRadius: 4, transition: 'width 0.4s' }} />
      </div>
      {showLabel && <span style={{ fontSize: 11, color: '#555', minWidth: 34, textAlign: 'right' }}>{v.toFixed(1)}%</span>}
    </div>
  );
}

// ════════════════════════════════════════════════════════════
//  COMPOSANT PRINCIPAL
// ════════════════════════════════════════════════════════════

// ── Hook tri tableau ──────────────────────────────────────────
function useSortTable(data, defaultCol = '') {
  const [sortCol, setSortCol] = React.useState(defaultCol);
  const [sortDir, setSortDir] = React.useState('asc');
  const handleSort = col => {
    if (sortCol === col) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortCol(col); setSortDir('asc'); }
  };
  const sorted = React.useMemo(() => {
    if (!sortCol || !data) return data || [];
    return [...data].sort((a, b) => {
      const va = (a[sortCol] ?? '').toString();
      const vb = (b[sortCol] ?? '').toString();
      const cmp = va.localeCompare(vb, 'fr', { sensitivity: 'base' });
      return sortDir === 'asc' ? cmp : -cmp;
    });
  }, [data, sortCol, sortDir]);
  const Th = ({ col, label, style = {}, className = '' }) => (
    <th onClick={() => handleSort(col)}
        className={className}
        style={{ cursor: 'pointer', userSelect: 'none', whiteSpace: 'nowrap', ...style }}
        title={`Trier par ${label}`}>
      {label}{' '}
      {sortCol === col
        ? (sortDir === 'asc' ? '▲' : '▼')
        : <span style={{ opacity: 0.25 }}>⇅</span>}
    </th>
  );
  return { sorted, Th, sortCol, sortDir };
}

export default function PointagePage() {
  const [subTab,     setSubTab]     = useState('saisie');   // saisie | stats | agent | params
  const [toast,      setToast]      = useState(null);
  const showToast = (msg, type = 'success') => setToast({ msg, type });

  // ── Saisie ───────────────────────────────────────────────
  const [dateJour,   setDateJour]   = useState(today());
  const [filterDept, setFilterDept] = useState('');
  const [filterStat, setFilterStat] = useState('');
  const [search,     setSearch]     = useState('');
  const [loading,    setLoading]    = useState(false);
  const [saving,     setSaving]     = useState(false);
  const [feuille,    setFeuille]    = useState(null);   // réponse API index
  const [edits,      setEdits]      = useState({});     // { empId: { statut, heure_arrivee, heure_depart, observation } }
  const [depts,      setDepts]      = useState([]);

  // ── Stats globales ───────────────────────────────────────
  const [statsDebut,  setStatsDebut]  = useState((() => { const d = new Date(); d.setDate(1); return d.toISOString().split('T')[0]; })());
  const [statsFin,    setStatsFin]    = useState(today());
  const [statsDept,   setStatsDept]   = useState('');
  const [statsData,   setStatsData]   = useState(null);
  const [statsLoad,   setStatsLoad]   = useState(false);

  // ── Fiche agent ─────────────────────────────────────────
  const [agentId,     setAgentId]     = useState('');
  const [agentDebut,  setAgentDebut]  = useState((() => { const d = new Date(); d.setDate(1); return d.toISOString().split('T')[0]; })());
  const [agentFin,    setAgentFin]    = useState(today());
  const [agentData,   setAgentData]   = useState(null);
  const [agentLoad,   setAgentLoad]   = useState(false);
  const [agentSearch, setAgentSearch] = useState('');
  const [agentList,   setAgentList]   = useState([]);

  // ── Paramètres ──────────────────────────────────────────
  const [params,      setParams]      = useState(null);
  const [paramsSaving,setParamsSaving]= useState(false);

  // ── Chargement référentiels ──────────────────────────────
  useEffect(() => {
    getDepartements().then(r => setDepts(r.data)).catch(() => {});
  }, []);

  // ── Chargement feuille de présence ──────────────────────
  const loadFeuille = useCallback(async () => {
    setLoading(true);
    try {
      const r = await getPointageJour({ date: dateJour, departement_id: filterDept, q: search });
      setFeuille(r.data);
      // Initialiser edits depuis les données existantes
      const init = {};
      r.data.data.forEach(row => {
        init[row.id] = {
          statut:        row.statut        || '',
          heure_arrivee: row.heure_arrivee ? row.heure_arrivee.slice(0, 5) : '',
          heure_depart:  row.heure_depart  ? row.heure_depart.slice(0, 5)  : '',
          observation:   row.observation   || '',
        };
      });
      setEdits(init);
    } catch { showToast('Erreur chargement feuille', 'error'); }
    setLoading(false);
  }, [dateJour, filterDept, search]);

  useEffect(() => {
    if (subTab === 'saisie') loadFeuille();
  }, [loadFeuille, subTab]);

  // ── Mise à jour locale d'une ligne ──────────────────────
  const setEdit = (empId, key, val) => {
    setEdits(prev => ({
      ...prev,
      [empId]: { ...prev[empId], [key]: val },
    }));
  };

  // ── Sauvegarder toute la feuille (batch) ────────────────
  const saveFeuille = async () => {
    setSaving(true);
    const user = JSON.parse(localStorage.getItem('gfm3_user') || '{}');
    const lignes = (feuille?.data || []).map(row => {
      const e = edits[row.id] || {};
      return {
        employe_id:    row.id,
        date_pointage: dateJour,
        heure_arrivee: e.heure_arrivee || null,
        heure_depart:  e.heure_depart  || null,
        statut:        e.statut        || null,
        observation:   e.observation   || null,
      };
    }).filter(l => l.statut); // n'envoyer que les lignes saisies

    try {
      await batchPointage({ pointages: lignes, saisi_par: user.id || null });
      showToast(`${lignes.length} pointage(s) enregistré(s)`);
      loadFeuille();
    } catch { showToast('Erreur enregistrement', 'error'); }
    setSaving(false);
  };

  // ── Sauvegarder une seule ligne ──────────────────────────
  const saveLine = async (empId) => {
    const e = edits[empId] || {};
    const user = JSON.parse(localStorage.getItem('gfm3_user') || '{}');
    try {
      await upsertPointage({
        employe_id:    empId,
        date_pointage: dateJour,
        heure_arrivee: e.heure_arrivee || null,
        heure_depart:  e.heure_depart  || null,
        statut:        e.statut        || null,
        observation:   e.observation   || null,
        saisi_par:     user.id         || null,
      });
      showToast('Pointage enregistré');
      loadFeuille();
    } catch { showToast('Erreur enregistrement', 'error'); }
  };

  // ── Tout marquer Present ─────────────────────────────────
  const marquerTousPresent = () => {
    const now  = new Date().toTimeString().slice(0, 5);
    setEdits(prev => {
      const next = { ...prev };
      (feuille?.data || []).forEach(row => {
        if (!next[row.id]?.statut) {
          next[row.id] = { ...next[row.id], statut: 'Present', heure_arrivee: now };
        }
      });
      return next;
    });
  };

  // ── Filtrage local ───────────────────────────────────────
  const rows = (feuille?.data || []).filter(row => {
    if (!filterStat) return true;
    const s = edits[row.id]?.statut || row.statut || 'Non saisi';
    return s === filterStat;
  });
  const { sorted: sortedRows, Th: ThP } = useSortTable(rows);

  // ── Chargement stats globales ────────────────────────────
  const loadStats = useCallback(async () => {
    setStatsLoad(true);
    try {
      const r = await getPointageStats({ debut: statsDebut, fin: statsFin, departement_id: statsDept });
      setStatsData(r.data);
    } catch { showToast('Erreur stats', 'error'); }
    setStatsLoad(false);
  }, [statsDebut, statsFin, statsDept]);

  useEffect(() => { if (subTab === 'stats') loadStats(); }, [loadStats, subTab]);

  // ── Fiche agent : recherche employés ────────────────────
  useEffect(() => {
    if (!agentSearch || agentSearch.length < 2) { setAgentList([]); return; }
    getPointageJour({ q: agentSearch, date: today() })
      .then(r => setAgentList(r.data.data || []))
      .catch(() => {});
  }, [agentSearch]);

  const loadAgent = useCallback(async () => {
    if (!agentId) return;
    setAgentLoad(true);
    try {
      const r = await getPointageAgent(agentId, { debut: agentDebut, fin: agentFin });
      setAgentData(r.data);
    } catch { showToast('Erreur fiche agent', 'error'); }
    setAgentLoad(false);
  }, [agentId, agentDebut, agentFin]);

  useEffect(() => { if (subTab === 'agent' && agentId) loadAgent(); }, [loadAgent, subTab]);

  // ── Paramètres ──────────────────────────────────────────
  const loadParams = useCallback(async () => {
    try { const r = await getPointageParametres(); setParams(r.data); } catch {}
  }, []);
  useEffect(() => { if (subTab === 'params') loadParams(); }, [loadParams, subTab]);

  const saveParams = async () => {
    setParamsSaving(true);
    try {
      await updatePointageParametres(params);
      showToast('Paramètres enregistrés');
    } catch { showToast('Erreur', 'error'); }
    setParamsSaving(false);
  };

  // ════════════════════════════════════════════════════════
  //  RENDU
  // ════════════════════════════════════════════════════════
  return (
    <>
      {toast && <Toast {...toast} onClose={() => setToast(null)} />}

      {/* ── En-tête de page ── */}
      <div className="page-header">
        <div>
          <h2>Gestion du Pointage</h2>
          <p>Saisie journalière, statistiques et suivi des présences</p>
        </div>
      </div>

      {/* ── Sous-onglets ── */}
      <div className="sub-tabs">
        {[
          { key: 'saisie', label: '📋 Saisie journalière' },
          { key: 'stats',  label: '📊 Statistiques globales' },
          { key: 'agent',  label: '👤 Fiche agent' },
          { key: 'params', label: '⚙️ Paramètres' },
        ].map(t => (
          <button
            key={t.key}
            className={`sub-tab${subTab === t.key ? ' active' : ''}`}
            onClick={() => setSubTab(t.key)}
          >
            {t.label}
          </button>
        ))}
      </div>

      {/* ════════════════════════════════════════════════════
          ONGLET 1 — SAISIE JOURNALIÈRE
      ════════════════════════════════════════════════════ */}
      {subTab === 'saisie' && (
        <>
          {/* Barre de contrôle */}
          <div className="card" style={{ marginBottom: 14 }}>
            {/* Ligne 1 : Date + Recherche + filtres + actions */}
            <div className="crud-bar" style={{ flexWrap: 'wrap', gap: 8 }}>
              {/* Date */}
              <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <label style={{ fontSize: 12, color: '#555', whiteSpace: 'nowrap' }}>Date :</label>
                <input
                  type="date" value={dateJour}
                  onChange={e => setDateJour(e.target.value)}
                  style={{ fontSize: 13, padding: '5px 8px', border: '1px solid #dde3ed', borderRadius: 6 }}
                />
              </div>
              {/* Département */}
              <select value={filterDept} onChange={e => setFilterDept(e.target.value)} style={{ fontSize: 13 }}>
                <option value="">Tous les départements</option>
                {depts.map(d => <option key={d.id} value={d.id}>{d.nom}</option>)}
              </select>
              {/* Filtre statut */}
              <select value={filterStat} onChange={e => setFilterStat(e.target.value)} style={{ fontSize: 13 }}>
                <option value="">Tous les statuts</option>
                {STATUTS.map(s => <option key={s} value={s}>{STATUT_LABELS[s]}</option>)}
                <option value="Non saisi">Non saisi</option>
              </select>
              {/* Recherche — style identique à EmployesPage */}
              <div className="crud-search-icon">
                <svg viewBox="0 0 24 24"><path d="M15.5 14h-.79l-.28-.27A6.47 6.47 0 0 0 16 9.5 6.5 6.5 0 1 0 9.5 16c1.61 0 3.09-.59 4.23-1.57l.27.28v.79l5 4.99L20.49 19l-4.99-5zm-6 0C7.01 14 5 11.99 5 9.5S7.01 5 9.5 5 14 7.01 14 9.5 11.99 14 9.5 14z" /></svg>
                <input
                  type="text" placeholder="Rechercher un agent…"
                  value={search} onChange={e => setSearch(e.target.value)}
                  style={{ width: 210 }}
                />
              </div>
              {/* Actions groupées à droite */}
              <div style={{ display: 'flex', gap: 8, marginLeft: 'auto', flexWrap: 'wrap', alignItems: 'center' }}>
                <button className="btn btn-gray btn-sm" onClick={marquerTousPresent} title="Marquer tout le monde présent">
                  ✅ Tous présents
                </button>
                <ExportPointage
                  type="feuille"
                  data={feuille}
                  date={dateJour}
                  edits={edits}
                />
                <button
                  className="btn btn-green"
                  onClick={saveFeuille}
                  disabled={saving}
                  style={{ minWidth: 160 }}
                >
                  {saving ? '⏳ Enregistrement…' : '💾 Enregistrer la feuille'}
                </button>
              </div>
            </div>
          </div>

          {/* KPI du jour */}
          {feuille && (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(130px,1fr))', gap: 10, marginBottom: 14 }}>
              {[
                { label: 'Effectif total', val: feuille.total,                            color: '#1565C0' },
                { label: 'Présents',       val: feuille.counts?.Present ?? 0,             color: '#2e7d32' },
                { label: 'Absents',        val: feuille.counts?.Absent  ?? 0,             color: '#c62828' },
                { label: 'Retards',        val: feuille.counts?.Retard  ?? 0,             color: '#f57f17' },
                { label: 'Non saisis',     val: feuille.counts?.['Non saisi'] ?? 0,       color: '#757575' },
                { label: 'Taux présence',  val: `${feuille.taux_presence ?? 0}%`,         color: '#1565C0' },
              ].map(({ label, val, color }) => (
                <div key={label} style={{
                  background: '#fff', border: '1px solid #e8edf3', borderRadius: 10,
                  padding: '12px 16px', borderTop: `3px solid ${color}`,
                }}>
                  <div style={{ fontSize: 11, color: '#888', marginBottom: 4, textTransform: 'uppercase', letterSpacing: .5 }}>{label}</div>
                  <div style={{ fontSize: 26, fontWeight: 700, color }}>{val}</div>
                </div>
              ))}
            </div>
          )}

          {/* Tableau de saisie */}
          <div className="card">
            <div className="table-wrap">
              {loading
                ? <div className="empty"><span className="spinner spinner-dark" /></div>
                : (
                  <table>
                    <thead>
                      <tr>
                        <ThP col="nom"         label="Employé(e)" />
                        <ThP col="matricule"   label="Matricule" />
                        <ThP col="departement" label="Département" />
                        <th style={{ minWidth: 130 }}>Statut</th>
                        <th style={{ minWidth: 100 }}>Arrivée</th>
                        <th style={{ minWidth: 100 }}>Départ</th>
                        <th style={{ minWidth: 160 }}>Observation</th>
                        <th style={{ textAlign: 'center' }}>Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {rows.length === 0
                        ? <tr><td colSpan={8} className="empty">Aucun agent trouvé</td></tr>
                        : sortedRows.map(row => {
                          const e = edits[row.id] || {};
                          return (
                            <tr key={row.id}>
                              {/* Nom */}
                              <td>
                                <div className="flex-c">
                                  <div className="avatar" style={{ width: 30, height: 30, fontSize: 11 }}>
                                    {row.nom?.[0]}{row.prenoms?.[0]}
                                  </div>
                                  <div>
                                    <div style={{ fontWeight: 700, fontSize: 13 }}>{row.nom} {row.prenoms}</div>
                                    <div style={{ fontSize: 11, color: '#888' }}>{row.fonction || '—'}</div>
                                  </div>
                                </div>
                              </td>
                              {/* Matricule */}
                              <td style={{ fontFamily: 'monospace', fontWeight: 700, color: '#1565C0', fontSize: 12 }}>
                                {row.matricule}
                              </td>
                              {/* Dept */}
                              <td style={{ fontSize: 12 }}>{row.departement || '—'}</td>
                              {/* Statut */}
                              <td>
                                <select
                                  value={e.statut || ''}
                                  onChange={ev => setEdit(row.id, 'statut', ev.target.value)}
                                  style={{
                                    fontSize: 12, padding: '4px 6px', borderRadius: 6,
                                    border: '1px solid #dde3ed', width: '100%',
                                    background: e.statut ? STATUT_COLORS[e.statut]?.bg : '#fff',
                                    color: e.statut ? STATUT_COLORS[e.statut]?.color : '#333',
                                    fontWeight: 600,
                                  }}
                                >
                                  <option value="">— Saisir —</option>
                                  {STATUTS.map(s => <option key={s} value={s}>{STATUT_LABELS[s]}</option>)}
                                </select>
                              </td>
                              {/* Heure arrivée */}
                              <td>
                                <input
                                  type="time" value={e.heure_arrivee || ''}
                                  onChange={ev => setEdit(row.id, 'heure_arrivee', ev.target.value)}
                                  style={{ fontSize: 12, padding: '4px 6px', borderRadius: 6, border: '1px solid #dde3ed', width: '100%' }}
                                />
                              </td>
                              {/* Heure départ */}
                              <td>
                                <input
                                  type="time" value={e.heure_depart || ''}
                                  onChange={ev => setEdit(row.id, 'heure_depart', ev.target.value)}
                                  style={{ fontSize: 12, padding: '4px 6px', borderRadius: 6, border: '1px solid #dde3ed', width: '100%' }}
                                />
                              </td>
                              {/* Observation */}
                              <td>
                                <input
                                  type="text" value={e.observation || ''}
                                  onChange={ev => setEdit(row.id, 'observation', ev.target.value)}
                                  placeholder="Motif…"
                                  style={{ fontSize: 12, padding: '4px 6px', borderRadius: 6, border: '1px solid #dde3ed', width: '100%' }}
                                />
                              </td>
                              {/* Action rapide */}
                              <td>
                                <div className="act-cell">
                                  <button
                                    className="btn btn-blue btn-sm"
                                    onClick={() => saveLine(row.id)}
                                    title="Enregistrer cette ligne"
                                    style={{ fontSize: 11, padding: '3px 8px' }}
                                  >
                                    ✓
                                  </button>
                                </div>
                              </td>
                            </tr>
                          );
                        })}
                    </tbody>
                  </table>
                )}
            </div>
          </div>
        </>
      )}

      {/* ════════════════════════════════════════════════════
          ONGLET 2 — STATISTIQUES GLOBALES
      ════════════════════════════════════════════════════ */}
      {subTab === 'stats' && (
        <>
          {/* Filtres */}
          <div className="card" style={{ marginBottom: 14 }}>
            <div className="crud-bar" style={{ flexWrap: 'wrap', gap: 10 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <label style={{ fontSize: 12, color: '#555' }}>Du :</label>
                <input type="date" value={statsDebut} onChange={e => setStatsDebut(e.target.value)}
                  style={{ fontSize: 13, padding: '5px 8px', border: '1px solid #dde3ed', borderRadius: 6 }} />
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <label style={{ fontSize: 12, color: '#555' }}>Au :</label>
                <input type="date" value={statsFin} onChange={e => setStatsFin(e.target.value)}
                  style={{ fontSize: 13, padding: '5px 8px', border: '1px solid #dde3ed', borderRadius: 6 }} />
              </div>
              <select value={statsDept} onChange={e => setStatsDept(e.target.value)} style={{ fontSize: 13 }}>
                <option value="">Tous les départements</option>
                {depts.map(d => <option key={d.id} value={d.id}>{d.nom}</option>)}
              </select>
              <button className="btn btn-blue" onClick={loadStats} disabled={statsLoad} style={{ marginLeft: 'auto' }}>
                {statsLoad ? '⏳ Chargement…' : '🔍 Actualiser'}
              </button>
              {statsData && !statsLoad && (
                <ExportPointage
                  type="stats"
                  data={statsData}
                  debut={statsDebut}
                  fin={statsFin}
                />
              )}
            </div>
          </div>

          {statsLoad && <div className="empty"><span className="spinner spinner-dark" /></div>}

          {statsData && !statsLoad && (
            <>
              {/* KPI */}
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(150px,1fr))', gap: 12, marginBottom: 18 }}>
                {[
                  { label: 'Employés actifs',  val: statsData.nb_employes, color: '#1565C0' },
                  { label: 'Jours présents',   val: statsData.par_statut?.Present    ?? 0, color: '#2e7d32' },
                  { label: 'Jours absents',    val: statsData.par_statut?.Absent     ?? 0, color: '#c62828' },
                  { label: 'Retards',          val: statsData.par_statut?.Retard     ?? 0, color: '#f57f17' },
                  { label: 'Congés',           val: statsData.par_statut?.Conge      ?? 0, color: '#6a1b9a' },
                  { label: 'Missions',         val: statsData.par_statut?.Mission    ?? 0, color: '#006064' },
                ].map(({ label, val, color }) => (
                  <div key={label} style={{
                    background: '#fff', border: '1px solid #e8edf3', borderRadius: 10,
                    padding: '14px 18px', borderTop: `3px solid ${color}`,
                  }}>
                    <div style={{ fontSize: 11, color: '#888', marginBottom: 4, textTransform: 'uppercase', letterSpacing: .5 }}>{label}</div>
                    <div style={{ fontSize: 28, fontWeight: 700, color }}>{val}</div>
                  </div>
                ))}
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 16 }}>

                {/* Évolution présences */}
                <div className="card">
                  <div className="card-title">📈 Évolution quotidienne — présences</div>
                  {statsData.evolution?.length > 0
                    ? (
                      <div style={{ overflowX: 'auto' }}>
                        <table style={{ width: '100%', fontSize: 12 }}>
                          <thead>
                            <tr>
                              <th style={{ textAlign: 'left', padding: '4px 8px', color: '#888', fontWeight: 600, fontSize: 11 }}>Date</th>
                              <th style={{ textAlign: 'center' }}>Présents</th>
                              <th style={{ textAlign: 'center' }}>Absents</th>
                              <th style={{ textAlign: 'center' }}>Retards</th>
                              <th style={{ minWidth: 100 }}>Taux</th>
                            </tr>
                          </thead>
                          <tbody>
                            {statsData.evolution.map(ev => {
                              const taux = ev.total_saisi > 0
                                ? Math.round((ev.presents / ev.total_saisi) * 100) : 0;
                              return (
                                <tr key={ev.jour}>
                                  <td style={{ padding: '5px 8px' }}>{fmtDate(ev.jour)}</td>
                                  <td style={{ textAlign: 'center', color: '#2e7d32', fontWeight: 600 }}>{ev.presents}</td>
                                  <td style={{ textAlign: 'center', color: '#c62828' }}>{ev.absents}</td>
                                  <td style={{ textAlign: 'center', color: '#f57f17' }}>{ev.retards}</td>
                                  <td style={{ paddingRight: 12 }}>
                                    <ProgressBar value={taux} color="#1565C0" />
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                    )
                    : <p style={{ color: '#aaa', fontSize: 13, padding: 12 }}>Aucune donnée sur la période.</p>
                  }
                </div>

                {/* Taux par département */}
                <div className="card">
                  <div className="card-title">🏢 Taux de présence par département</div>
                  {statsData.par_departement?.length > 0
                    ? (
                      <div>
                        {statsData.par_departement.map(d => (
                          <div key={d.departement} style={{ marginBottom: 10 }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, marginBottom: 3 }}>
                              <span style={{ fontWeight: 600 }}>{d.departement || 'Sans département'}</span>
                              <span style={{ color: '#555' }}>
                                {d.presents}/{d.total_lignes} ({d.taux_presence}%)
                              </span>
                            </div>
                            <ProgressBar
                              value={d.taux_presence}
                              color={parseFloat(d.taux_presence) >= 80 ? '#2e7d32' : parseFloat(d.taux_presence) >= 60 ? '#f57f17' : '#c62828'}
                            />
                          </div>
                        ))}
                      </div>
                    )
                    : <p style={{ color: '#aaa', fontSize: 13, padding: 12 }}>Aucune donnée.</p>
                  }
                </div>
              </div>

              {/* Top absents */}
              <div className="card">
                <div className="card-title">⚠️ Agents avec le plus d'absences sur la période</div>
                {statsData.top_absents?.length > 0
                  ? (
                    <div className="table-wrap">
                      <table>
                        <thead>
                          <tr>
                            <th>Agent</th>
                            <th>Département</th>
                            <th style={{ textAlign: 'center' }}>Absences</th>
                            <th style={{ textAlign: 'center' }}>Retards</th>
                            <th style={{ textAlign: 'center' }}>Jours saisis</th>
                            <th style={{ minWidth: 130 }}>Taux absence</th>
                            <th style={{ textAlign: 'center' }}>Fiche</th>
                          </tr>
                        </thead>
                        <tbody>
                          {statsData.top_absents.map(a => (
                            <tr key={a.id}>
                              <td style={{ fontWeight: 600, fontSize: 13 }}>{a.employe}</td>
                              <td style={{ fontSize: 12 }}>{a.departement || '—'}</td>
                              <td style={{ textAlign: 'center' }}>
                                <span style={{ color: '#c62828', fontWeight: 700 }}>{a.nb_absences}</span>
                              </td>
                              <td style={{ textAlign: 'center' }}>
                                <span style={{ color: '#f57f17', fontWeight: 600 }}>{a.nb_retards}</span>
                              </td>
                              <td style={{ textAlign: 'center', fontSize: 12 }}>{a.nb_jours_saisis}</td>
                              <td>
                                <ProgressBar
                                  value={a.taux_absence}
                                  color={parseFloat(a.taux_absence) > 30 ? '#c62828' : '#f57f17'}
                                />
                              </td>
                              <td style={{ textAlign: 'center' }}>
                                <button
                                  className="btn btn-blue btn-sm"
                                  onClick={() => { setAgentId(String(a.id)); setSubTab('agent'); }}
                                  style={{ fontSize: 11 }}
                                >
                                  Voir
                                </button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )
                  : <p style={{ color: '#aaa', fontSize: 13, padding: 12 }}>Aucune absence sur la période.</p>
                }
              </div>

              {/* Tableau individuel complet */}
              <div className="card" style={{ marginTop: 16 }}>
                <div className="card-title">👥 Récapitulatif individuel</div>
                <div className="table-wrap">
                  <table>
                    <thead>
                      <tr>
                        <th>Agent</th>
                        <th>Département</th>
                        <th style={{ textAlign: 'center' }}>Présents</th>
                        <th style={{ textAlign: 'center' }}>Absents</th>
                        <th style={{ textAlign: 'center' }}>Retards</th>
                        <th style={{ textAlign: 'center' }}>Congés</th>
                        <th style={{ textAlign: 'center' }}>Missions</th>
                        <th style={{ minWidth: 130 }}>Taux présence</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(statsData.individuel || []).map(a => (
                        <tr key={a.id} style={{ cursor: 'pointer' }}
                          onClick={() => { setAgentId(String(a.id)); setSubTab('agent'); }}
                          title="Voir la fiche de cet agent"
                        >
                          <td style={{ fontWeight: 600, fontSize: 13 }}>{a.employe}</td>
                          <td style={{ fontSize: 12 }}>{a.departement || '—'}</td>
                          <td style={{ textAlign: 'center', color: '#2e7d32', fontWeight: 600 }}>{a.presents}</td>
                          <td style={{ textAlign: 'center', color: '#c62828' }}>{a.absences}</td>
                          <td style={{ textAlign: 'center', color: '#f57f17' }}>{a.retards}</td>
                          <td style={{ textAlign: 'center', color: '#6a1b9a' }}>{a.conges}</td>
                          <td style={{ textAlign: 'center', color: '#006064' }}>{a.missions}</td>
                          <td>
                            <ProgressBar
                              value={a.taux_presence}
                              color={parseFloat(a.taux_presence) >= 80 ? '#2e7d32' : parseFloat(a.taux_presence) >= 60 ? '#f57f17' : '#c62828'}
                            />
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </>
          )}
        </>
      )}

      {/* ════════════════════════════════════════════════════
          ONGLET 3 — FICHE AGENT
      ════════════════════════════════════════════════════ */}
      {subTab === 'agent' && (
        <>
          {/* Sélection agent */}
          <div className="card" style={{ marginBottom: 14 }}>
            <div className="crud-bar" style={{ flexWrap: 'wrap', gap: 10 }}>
              {/* Recherche agent avec autocomplete */}
              <div style={{ position: 'relative', flex: 1, minWidth: 220 }}>
                <div className="crud-search-icon">
                  <svg viewBox="0 0 24 24"><path d="M15.5 14h-.79l-.28-.27A6.47 6.47 0 0 0 16 9.5 6.5 6.5 0 1 0 9.5 16c1.61 0 3.09-.59 4.23-1.57l.27.28v.79l5 4.99L20.49 19l-4.99-5zm-6 0C7.01 14 5 11.99 5 9.5S7.01 5 9.5 5 14 7.01 14 9.5 11.99 14 9.5 14z" /></svg>
                  <input
                    type="text" placeholder="Rechercher un agent…"
                    value={agentSearch}
                    onChange={e => setAgentSearch(e.target.value)}
                    style={{ minWidth: 200 }}
                  />
                </div>
                {agentList.length > 0 && agentSearch.length >= 2 && (
                  <div style={{
                    position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 100,
                    background: '#fff', border: '1px solid #dde3ed', borderRadius: 8,
                    boxShadow: '0 4px 16px rgba(0,0,0,.1)', maxHeight: 220, overflowY: 'auto',
                  }}>
                    {agentList.map(a => (
                      <div key={a.id}
                        onClick={() => { setAgentId(String(a.id)); setAgentSearch(`${a.nom} ${a.prenoms}`); setAgentList([]); }}
                        style={{
                          padding: '9px 14px', cursor: 'pointer', fontSize: 13,
                          borderBottom: '1px solid #f0f2f5',
                        }}
                        onMouseEnter={e => e.currentTarget.style.background = '#f0f6ff'}
                        onMouseLeave={e => e.currentTarget.style.background = '#fff'}
                      >
                        <strong>{a.nom} {a.prenoms}</strong>
                        <span style={{ color: '#888', fontSize: 11, marginLeft: 8 }}>{a.matricule} · {a.departement || '—'}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
              {/* Période */}
              <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <label style={{ fontSize: 12, color: '#555' }}>Du :</label>
                <input type="date" value={agentDebut} onChange={e => setAgentDebut(e.target.value)}
                  style={{ fontSize: 13, padding: '5px 8px', border: '1px solid #dde3ed', borderRadius: 6 }} />
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <label style={{ fontSize: 12, color: '#555' }}>Au :</label>
                <input type="date" value={agentFin} onChange={e => setAgentFin(e.target.value)}
                  style={{ fontSize: 13, padding: '5px 8px', border: '1px solid #dde3ed', borderRadius: 6 }} />
              </div>
              <button className="btn btn-blue" onClick={loadAgent} disabled={!agentId || agentLoad}>
                {agentLoad ? '⏳ Chargement…' : '🔍 Afficher'}
              </button>
              {agentData && !agentLoad && (
                <ExportPointage type="agent" data={agentData} />
              )}
            </div>
          </div>

          {agentLoad && <div className="empty"><span className="spinner spinner-dark" /></div>}

          {agentData && !agentLoad && (
            <>
              {/* En-tête agent */}
              <div className="card" style={{ marginBottom: 14, background: 'linear-gradient(135deg, #1a3a5c 0%, #1565C0 100%)', color: '#fff', border: 'none' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
                  <div className="avatar" style={{ width: 52, height: 52, fontSize: 18, background: 'rgba(255,255,255,.2)', color: '#fff', border: '2px solid rgba(255,255,255,.4)' }}>
                    {agentData.employe?.employe?.split(' ').map(w => w[0]).join('').slice(0, 2).toUpperCase()}
                  </div>
                  <div>
                    <div style={{ fontSize: 18, fontWeight: 700 }}>{agentData.employe?.employe}</div>
                    <div style={{ fontSize: 13, opacity: .8 }}>
                      {agentData.employe?.matricule} · {agentData.employe?.departement || '—'} · {agentData.employe?.fonction || '—'}
                    </div>
                    <div style={{ fontSize: 12, opacity: .7, marginTop: 3 }}>
                      Période : {fmtDate(agentData.periode?.debut)} → {fmtDate(agentData.periode?.fin)}
                    </div>
                  </div>
                </div>
              </div>

              {/* Stats résumées */}
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(130px,1fr))', gap: 10, marginBottom: 14 }}>
                {[
                  { label: 'Taux présence',  val: fmtPct(agentData.stats?.taux_presence),  color: '#1565C0' },
                  { label: 'Taux absence',   val: fmtPct(agentData.stats?.taux_absence),   color: '#c62828' },
                  { label: 'Jours présents', val: agentData.stats?.presents  ?? 0,          color: '#2e7d32' },
                  { label: 'Absences',       val: agentData.stats?.absences  ?? 0,          color: '#c62828' },
                  { label: 'Retards',        val: agentData.stats?.retards   ?? 0,          color: '#f57f17' },
                  { label: 'Congés',         val: agentData.stats?.conges    ?? 0,          color: '#6a1b9a' },
                  { label: 'Missions',       val: agentData.stats?.missions  ?? 0,          color: '#006064' },
                  { label: 'Demi-journées',  val: agentData.stats?.demi_journees ?? 0,      color: '#1565C0' },
                ].map(({ label, val, color }) => (
                  <div key={label} style={{
                    background: '#fff', border: '1px solid #e8edf3', borderRadius: 10,
                    padding: '12px 14px', borderTop: `3px solid ${color}`,
                  }}>
                    <div style={{ fontSize: 10, color: '#888', marginBottom: 4, textTransform: 'uppercase', letterSpacing: .5 }}>{label}</div>
                    <div style={{ fontSize: 22, fontWeight: 700, color }}>{val}</div>
                  </div>
                ))}
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 16 }}>
                {/* Infos horaires */}
                <div className="card">
                  <div className="card-title">⏱ Moyennes horaires</div>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                    {[
                      { label: "Heure d'arrivée moy.", val: fmtTime(agentData.stats?.heure_arrivee_moy) },
                      { label: 'Durée moy. journée',   val: fmtTime(agentData.stats?.duree_moy_journee) },
                      { label: 'Jours saisis',          val: agentData.stats?.jours_saisis ?? 0 },
                    ].map(({ label, val }) => (
                      <div key={label} style={{ background: '#f8fafc', borderRadius: 8, padding: '10px 12px', border: '1px solid #e8edf3' }}>
                        <div style={{ fontSize: 11, color: '#888', marginBottom: 3 }}>{label}</div>
                        <div style={{ fontSize: 18, fontWeight: 700, color: '#1a3a5c' }}>{val}</div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Évolution mensuelle */}
                <div className="card">
                  <div className="card-title">📈 Évolution 6 derniers mois</div>
                  {agentData.evolution_mois?.length > 0
                    ? (
                      <table style={{ width: '100%', fontSize: 12 }}>
                        <thead>
                          <tr>
                            <th style={{ textAlign: 'left', padding: '3px 6px', color: '#888', fontSize: 11 }}>Mois</th>
                            <th>Présents</th><th>Absents</th><th>Retards</th>
                            <th style={{ minWidth: 80 }}>Taux</th>
                          </tr>
                        </thead>
                        <tbody>
                          {agentData.evolution_mois.map(m => {
                            const taux = m.total > 0 ? Math.round((m.presents / m.total) * 100) : 0;
                            return (
                              <tr key={m.mois}>
                                <td style={{ padding: '4px 6px', fontWeight: 600 }}>{m.mois}</td>
                                <td style={{ textAlign: 'center', color: '#2e7d32' }}>{m.presents}</td>
                                <td style={{ textAlign: 'center', color: '#c62828' }}>{m.absences}</td>
                                <td style={{ textAlign: 'center', color: '#f57f17' }}>{m.retards}</td>
                                <td><ProgressBar value={taux} color="#1565C0" /></td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    )
                    : <p style={{ color: '#aaa', fontSize: 13 }}>Pas d'historique.</p>
                  }
                </div>
              </div>

              {/* Historique détaillé */}
              <div className="card">
                <div className="card-title">📅 Historique détaillé — {agentData.historique?.length ?? 0} enregistrement(s)</div>
                <div className="table-wrap">
                  <table>
                    <thead>
                      <tr>
                        <th>Date</th>
                        <th>Statut</th>
                        <th style={{ textAlign: 'center' }}>Arrivée</th>
                        <th style={{ textAlign: 'center' }}>Départ</th>
                        <th>Observation</th>
                        <th style={{ textAlign: 'center' }}>Durée</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(agentData.historique || []).length === 0
                        ? <tr><td colSpan={6} className="empty">Aucun pointage sur la période</td></tr>
                        : (agentData.historique || []).map(h => {
                          let duree = '—';
                          if (h.heure_arrivee && h.heure_depart) {
                            const [ah, am] = h.heure_arrivee.split(':').map(Number);
                            const [dh, dm] = h.heure_depart.split(':').map(Number);
                            const diff = (dh * 60 + dm) - (ah * 60 + am);
                            if (diff > 0) duree = `${Math.floor(diff / 60)}h${String(diff % 60).padStart(2, '0')}`;
                          }
                          return (
                            <tr key={h.id}>
                              <td style={{ fontWeight: 600 }}>{fmtDate(h.date_pointage)}</td>
                              <td><StatutBadge statut={h.statut} /></td>
                              <td style={{ textAlign: 'center', fontFamily: 'monospace', fontSize: 13 }}>
                                {fmtTime(h.heure_arrivee)}
                              </td>
                              <td style={{ textAlign: 'center', fontFamily: 'monospace', fontSize: 13 }}>
                                {fmtTime(h.heure_depart)}
                              </td>
                              <td style={{ fontSize: 12, color: '#666' }}>{h.observation || '—'}</td>
                              <td style={{ textAlign: 'center', fontSize: 12, fontFamily: 'monospace' }}>{duree}</td>
                            </tr>
                          );
                        })}
                    </tbody>
                  </table>
                </div>
              </div>
            </>
          )}

          {!agentData && !agentLoad && (
            <div className="empty" style={{ marginTop: 40 }}>
              <div style={{ fontSize: 15, color: '#aaa' }}>
                Recherchez un agent ci-dessus et cliquez sur <strong>Afficher</strong>
              </div>
            </div>
          )}
        </>
      )}

      {/* ════════════════════════════════════════════════════
          ONGLET 4 — PARAMÈTRES
      ════════════════════════════════════════════════════ */}
      {subTab === 'params' && (
        <div className="card" style={{ maxWidth: 520 }}>
          <div className="card-title">⚙️ Paramètres horaires du pointage</div>
          {!params
            ? <div className="empty"><span className="spinner spinner-dark" /></div>
            : (
              <>
                <div className="form-grid">
                  <div className="fg">
                    <label>Heure d'arrivée réglementaire</label>
                    <input type="time" value={params.heure_arrivee || '08:00'}
                      onChange={e => setParams(p => ({ ...p, heure_arrivee: e.target.value }))} />
                  </div>
                  <div className="fg">
                    <label>Heure de départ réglementaire</label>
                    <input type="time" value={params.heure_depart || '17:30'}
                      onChange={e => setParams(p => ({ ...p, heure_depart: e.target.value }))} />
                  </div>
                </div>
                <div className="form-grid">
                  <div className="fg">
                    <label>Tolérance retard (minutes)</label>
                    <input type="number" min={0} max={60} value={params.tolerance_retard ?? 15}
                      onChange={e => setParams(p => ({ ...p, tolerance_retard: parseInt(e.target.value) }))} />
                    <small style={{ color: '#888', fontSize: 11 }}>
                      Arrivées dans ce délai après l'heure réglementaire sont marquées "Présent" et non "Retard".
                    </small>
                  </div>
                  <div className="fg">
                    <label>Jours ouvrés (1=Lun…7=Dim)</label>
                    <input type="text" value={params.jours_ouvres || '1,2,3,4,5'}
                      onChange={e => setParams(p => ({ ...p, jours_ouvres: e.target.value }))}
                      placeholder="1,2,3,4,5" />
                    <small style={{ color: '#888', fontSize: 11 }}>Séparés par des virgules. Ex : 1,2,3,4,5 = Lundi→Vendredi</small>
                  </div>
                </div>
                <div className="actions-bar" style={{ marginTop: 16 }}>
                  <button className="btn btn-green" onClick={saveParams} disabled={paramsSaving}>
                    {paramsSaving ? '⏳…' : '💾 Enregistrer'}
                  </button>
                </div>
              </>
            )}
        </div>
      )}
    </>
  );
}
